'use client';

import type { ClassValue } from 'clsx';

import { useAtomSet } from '@effect/atom-react';
import { GlossaryIndexEmpty, GlossaryIndexNoticeMessage } from '@vs-shop/commerce-ui/glossaryIndex';
import { Button } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';
import { Match } from 'effect';
import { useIntl } from 'react-intl';

import { GlossaryMode } from '../../../../effect/data/glossary/glossaryMode';
import { glossaryFilterActionFn } from '../../../../effect/atom/glossary/glossarySearchInput';

type Props = {
  mode: GlossaryMode;
  searchQuery?: string | null;
  letter?: string | null;
  className?: ClassValue;
};

export function GlossaryEmptyState({ mode, searchQuery, letter, className }: Props) {
  const intl = useIntl();

  const dispatchFilter = useAtomSet(glossaryFilterActionFn, { mode: 'promise' });

  const clearAllFilters = () => {
    void dispatchFilter({ _tag: 'Clear' });
  };

  const message = Match.value(mode).pipe(
    Match.when(GlossaryMode.search, () =>
      intl.formatMessage(
        {
          id: 'component.client.page.glossary.empty.search',
          defaultMessage: 'Žádné pojmy neodpovídají "{query}"',
        },
        { query: searchQuery ?? '' }
      )
    ),
    Match.when(GlossaryMode.alphabet, () =>
      intl.formatMessage(
        {
          id: 'component.client.page.glossary.empty.alphabet',
          defaultMessage: 'Žádné pojmy na písmeno "{letter}"',
        },
        { letter: letter ?? '' }
      )
    ),
    Match.when(GlossaryMode.favorites, () =>
      intl.formatMessage({
        id: 'component.client.page.glossary.empty.favorites',
        defaultMessage: 'Zatím zde nejsou žádné oblíbené pojmy.',
      })
    ),
    Match.exhaustive
  );

  const showClearButton = mode !== GlossaryMode.favorites;

  return (
    <GlossaryIndexEmpty className={clsxm(className)}>
      <GlossaryIndexNoticeMessage>{message}</GlossaryIndexNoticeMessage>
      {showClearButton && (
        <Button onClick={clearAllFilters}>
          {intl.formatMessage({
            id: 'component.client.page.glossary.empty.clear',
            defaultMessage: 'Vymazat filtr',
          })}
        </Button>
      )}
    </GlossaryIndexEmpty>
  );
}
